import memoize from "memoizee";

/** * TODO: Explain what you mean by 'refinements' and what this module is
 * for.
 *
 * These functions just wrangle upstream data and set the _initial
 * structure_ of the data in the state tree. Mostly, they do not (and if
 * you think about about, *should* not) do much other than restructure
 * upstream API responses.
 */

/**
 * Imported separately to emphasize that these are the "Core Entity
 * Types" we expose to the developers who use our library.
 */
import {
  Crop,
  Enrollment,
  Member,
  Operation,
  User,
  Permissions,
  Role,
} from "../../models";
import {
  ApplicationPermissions,
  RawApplication,
  RawApplicationPermissions,
} from "../../models/applications";
import { RAW_TO_REFINED_APPLICATION_NAMES } from "../../constants";

/**
 * Imported this way since we *do not* expose these types to our
 * developers. These are for us, our little implementation details,
 * our little secrets 😉🤗
 */
import {
  OperationResponse,
  OperationResponseV4,
  OperationV4,
} from "../../models/operations";
import { EnrollmentResponse } from "../../models/enrollments";
import { FieldResponse, BasicField } from "../../models/fields";
import { MemberResponse } from "../../models/members";
import { CropResponse } from "../../models/crops";
import {
  FieldToCropMappingResponse,
  FieldToCropMapping,
} from "../../models/mappings";
import {
  InviteResponse,
  Invite,
  RawBatchInviteNetworkResponse,
  BatchInvite,
} from "../../models/invitations";
import { BasicFarm, FarmResponse } from "../../models/farms";

export const refineApplicationPermissions = (
  permissions?: RawApplicationPermissions,
): ApplicationPermissions | undefined => {
  if (!permissions) {
    return undefined;
  }
  const ret: Partial<ApplicationPermissions> = {};

  (Object.keys(permissions) as Array<RawApplication>).map((_) => {
    const refinedAppName = RAW_TO_REFINED_APPLICATION_NAMES[_];
    ret[refinedAppName] = permissions[_];
  });

  return ret as ApplicationPermissions;
};

/**
 * Why are we doing this? It has to do with the Roles Service. There are
 * "base roles" in the service which *do not always mean the same role*
 * in an *application context* (like Insights, for example.)
 *
 * So we're just trying to be explicit in our Store. When the developer
 * uses this stuff, they'd do something like:
 *
 *    {operation.permissions.isAdmin && <Component>}
 */
export const refinedOperation_NOT_MEMOIZED = (
  operation: OperationResponse,
): Operation => ({
  id: operation.id,
  name: operation.name,
  permissions: {
    isAdvisor: operation.user_role.base_role === "advisor",
    isAdmin: operation.user_role.base_role === "administrator",
    isEditor: operation.user_role.base_role === "editor",
    isViewer: operation.user_role.base_role === "viewer",
    canViewFinancials: operation.user_role.financial,
    canAccessNitrogen: operation.user_role.nitrogen,
  },
  settings: operation.settings,
  applicationPermissions: refineApplicationPermissions(
    operation.user_permission ?? {},
  ),
  user_permission: operation.user_permission ?? {},
  user_role: { base_role: operation.user_role.base_role as Role },
});

// v4
export const refinedOperation_NOT_MEMOIZEDV4 = (
  operation: OperationResponseV4,
): OperationV4 => ({
  id: operation.id,
  name: operation.name,
  permissions: operation.permissions,
  settings: operation.settings,
});

export const operationNameComparator = (
  operationOne: Operation,
  operationTwo: Operation,
): number => {
  const a = operationOne.name.toLowerCase();
  const b = operationTwo.name.toLowerCase();

  return a < b ? -1 : a > b ? 1 : 0;
};

export const operationPermissionComparator = (
  operationOne: Operation,
  operationTwo: Operation,
): number => {
  const a = operationOne.permissions.isAdmin;
  const b = operationTwo.permissions.isAdmin;

  return a < b ? 1 : a > b ? -1 : 0;
};

// Why 1? Because we only have 1 active list of operations at a time.
export const numberOfOperationsListsPossiblyActiveAtTheSameTime = 1;

/**
 * Refine each operation. Then sort by name and Insights admin permissions.
 * That last part might not be necessary.
 */
export const refinedListOfOperations_NOT_MEMOIZED = (
  listOfOperationResponses: OperationResponse[],
): Operation[] =>
  listOfOperationResponses.map(
    (op): Operation => refinedOperation_NOT_MEMOIZED(op),
  );

export const refinedListOfOperations = memoize(
  refinedListOfOperations_NOT_MEMOIZED,
  {
    max: numberOfOperationsListsPossiblyActiveAtTheSameTime,
    profileName: "[refinements] refinedListOfOperations",
  },
);

export const sortedListOfOperations = memoize(
  (listOfOperations: Operation[]): Operation[] =>
    listOfOperations
      .sort(operationNameComparator)
      .sort(operationPermissionComparator),
  {
    // Why 2x? Because this is run twice for slightly different versions of the list:
    // once for the preOperations list and once for the operations list.
    max: 2 * numberOfOperationsListsPossiblyActiveAtTheSameTime,
    profileName: "[refinements] sortedListOfOperations",
  },
);

// Why 1? Because we only have 1 active list of field enrollments at a time.
export const numberOfFieldEnrollmentsListsPossiblyActiveAtTheSameTime = 1;

// Why 1? Because we only have 1 active list of enrollments at a time.
export const numberOfEnrollmentsListsPossiblyActiveAtTheSameTime = 1;

export const refinedListOfEnrollments = memoize(
  (enrollments: EnrollmentResponse[]): Enrollment[] =>
    enrollments.map(
      (e: EnrollmentResponse): Enrollment => ({
        currencyCode: e.currency_code,
        endDate: e.end_date,
        entityId: e.entity_id,
        featureId: e.feature_id,
        id: e.id,
        operationId: e.operation_id,
        source: e.source,
        startDate: e.start_date,
        status: e.status,
        totalArea: e.total_area,
        totalPrice: e.total_price,
      }),
    ),
  {
    max: numberOfEnrollmentsListsPossiblyActiveAtTheSameTime,
    profileName: "[refinements] refinedListOfEnrollments",
  },
);
(window as any).fabricRefinedListOfEnrollments = refinedListOfEnrollments;

const refinedField = (field: FieldResponse): BasicField => ({
  area: field.area,
  areaCalculation: field.area_calculation,
  end: field.end,
  farmId: field.farm_id,
  id: field.field_id,
  latitude: field.latitude,
  longitude: field.longitude,
  name: field.name,
  revisionId: field.revision_id,
  shape: field.shape,
  thumbnailSvg: field.thumbnail_svg,
  source: field.source,
  start: field.start,
  zoneId: field.zone_id,
});

// Why 1? Because we only have 1 active list of fields at a time.
export const numberOfFieldsListsPossiblyActiveAtTheSameTime = 1;

/**
 * Structure the response from the Fields Service. Sets up the basic
 * farm object if it exists (ie. the Farm ID.)
 *
 * @param listOfFields A list of fields from the Fields Service to refine
 */
export const refinedListOfFields = memoize(
  (listOfFields: FieldResponse[]): BasicField[] =>
    listOfFields.map((field) => refinedField(field)),
  {
    max: numberOfFieldsListsPossiblyActiveAtTheSameTime,
    profileName: "[refinements] refinedListOfFields",
  },
);

/**
 * Sort the members by their first names
 */
export const memberNameComparator = (
  memberOne: Member,
  memberTwo: Member,
): number => {
  const a = memberOne.firstName.toLowerCase();
  const b = memberTwo.firstName.toLowerCase();

  return a < b ? -1 : a > b ? 1 : 0;
};

/**
 * Create a Member object consistent with the names of the fields in the
 * `User` type and the permissions in the `Operation` type.
 */
export const refinedMember = (
  member: MemberResponse,
  currentUserId: string,
): Member => ({
  firstName: member.first,
  lastName: member.last,
  email: member.email,
  phone: member.phone,
  granularId: member.granular_id,
  isSelf: member.granular_id === currentUserId,
  permissions: {
    isAdvisor: member.user_role.base_role === "advisor",
    isAdmin: member.user_role.base_role === "administrator",
    isEditor: member.user_role.base_role === "editor",
    isViewer: member.user_role.base_role === "viewer",
    canViewFinancials: member.user_role.financial,
    canAccessNitrogen: member.user_role.nitrogen,
  },
  cortevaType: member.corteva_type || null,
  brandName: member.brand_name || null,
});

// Why 1? Because we only have 1 active list of members at a time.
export const numberOfMembersListsPossiblyActiveAtTheSameTime = 1;

export const refinedListOfMembers = memoize(
  (listOfMembers: MemberResponse[], currentUserId: string): Member[] => {
    return listOfMembers.map((x) => refinedMember(x, currentUserId));
  },
  {
    max: numberOfMembersListsPossiblyActiveAtTheSameTime,
    profileName: "[refinements] refinedListOfMembers",
  },
);

/**
 * Generate a list of members where:
 *  1) The currently logged-in user (if exists) is at the top of the list.
 *  2) Any/all other members are sorted by their first names.
 */
export const sortedListOfMembers = memoize(
  (members: Member[], currentUserAsMember?: Member): Member[] => {
    if (currentUserAsMember) {
      const membersWithoutCurrentUser = members.filter(
        (x) => x.granularId !== currentUserAsMember.granularId,
      );
      return [
        currentUserAsMember,
        ...membersWithoutCurrentUser.sort(memberNameComparator),
      ];
    }
    return [...members].sort(memberNameComparator);
  },
  {
    max: numberOfMembersListsPossiblyActiveAtTheSameTime,
    profileName: "[refinements] sortedListOfMembers",
  },
);

export const convertCurrentUserToMember = memoize(
  (
    currentUser: User,
    permissions: Permissions,
    brandName: string | null,
    cortevaType: string | null,
  ): Member => {
    return {
      granularId: currentUser.granularId,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      email: currentUser.email,
      phone: currentUser.phoneNumber,
      isSelf: true,
      permissions,
      // We are unable to get these pieces of information
      // from the current user object so we will simply use the given values.
      brandName,
      cortevaType,
    };
  },
  {
    max: numberOfMembersListsPossiblyActiveAtTheSameTime,
    profileName: "[refinements] convertCurrentUserToMember",
  },
);

/**
 * TODO: This needs to be done by upstream! Bug someone on CDC to give
 * us an alphabetic sort!
 */
export const cropNameComparator = (cropOne: Crop, cropTwo: Crop): number => {
  const a = cropOne.name.toLowerCase();
  const b = cropTwo.name.toLowerCase();

  return a < b ? -1 : a > b ? 1 : 0;
};

// Why 1? Because we only have 1 active list of crops at a time.
export const numberOfCropsListsPossiblyActiveAtTheSameTime = 1;

export const refinedListOfCrops = memoize(
  (listOfCropResponses: CropResponse[]): Crop[] =>
    listOfCropResponses.map((crop) => ({
      baseUnit: crop.base_unit,
      name: crop.crop,
      id: crop.id,
    })),
  {
    max: numberOfCropsListsPossiblyActiveAtTheSameTime,
    profileName: "[refinements] refinedListOfCrops",
  },
);

export const sortedListOfCrops = memoize(
  (listOfCrops: Crop[]): Crop[] => listOfCrops.sort(cropNameComparator),
  {
    max: numberOfCropsListsPossiblyActiveAtTheSameTime,
    profileName: "[refinements] sortedListOfCrops",
  },
);

// Why 1? Because we only have 1 active list of field crops at a time.
export const numberOfFieldCropsListsPossiblyActiveAtTheSameTime = 1;

export const refinedFieldToCropMapping = memoize(
  (mapping: FieldToCropMappingResponse): FieldToCropMapping => {
    const result: FieldToCropMapping = {};

    Object.keys(mapping).forEach(
      (fieldId) =>
        (result[fieldId] = {
          fieldId,
          cropId: mapping[fieldId].crop_id,
          cropPlanId: mapping[fieldId].id,
          isActual: mapping[fieldId].is_actual,
        }),
    );

    return result;
  },
  {
    max: numberOfFieldCropsListsPossiblyActiveAtTheSameTime,
    profileName: "[refinements] refinedFieldToCropMapping",
  },
);

// Why 1? Because we only have 1 active list of invites at a time.
export const numberOfInvitesListsPossiblyActiveAtTheSameTime = 1;

export const refinedListOfPendingInvitations = memoize(
  (listOfPendingInvites: InviteResponse[] | null): Invite[] => {
    if (listOfPendingInvites === null) {
      /*
       * Also note that, *in the Core only and for #simplicity*, we return
       * an empty list if the user is not allowed to view invites for an operation.
       *
       * This is not a terrible decision. In a UI, you would simply not render an
       * invites component if `!isAdmin` 🤷‍♂️
       */
      return [];
    }
    return listOfPendingInvites.map((invite) => ({
      expiresOn: invite.expirationTime,
      email: invite.userName,
      permissions: {
        isAdvisor: invite.roleName === "advisor",
        isAdmin: invite.roleName === "administrator",
        isEditor: invite.roleName === "editor",
        isViewer: invite.roleName === "viewer",

        /**
         * This is assumed by default until there is a UI change. I.e.,
         * imagine a checkbox in the invite interface that says "Allow
         * invitee to view financials."
         * Admins get financials by default
         * Advisors get Nitrogen by default
         */
        canViewFinancials: invite.roleName === "administrator",
        canAccessNitrogen: invite.roleName === "advisor",
      },
    }));
  },
  {
    max: numberOfInvitesListsPossiblyActiveAtTheSameTime,
    profileName: "[refinements] refinedListOfPendingInvitations",
  },
);

const refinedFarm = (farm: FarmResponse): BasicFarm => ({
  id: farm.id,
  name: farm.name,
  fieldCount: farm.field_count,
});

// Why 1? Because we only have 1 active list of farms at a time.
export const numberOfFarmsListsPossiblyActiveAtTheSameTime = 1;

export const refinedListOfFarms = memoize(
  (listOfFarms: FarmResponse[]): BasicFarm[] =>
    listOfFarms.map((farm) => refinedFarm(farm)),
  {
    max: numberOfFarmsListsPossiblyActiveAtTheSameTime,
    profileName: "[refinements] refinedListOfFarms",
  },
);

/**
 * NOTE: This is in the context of a single operation, the Current Operation!
 * We simply discriminate the union (see the type definitions) and refine
 * the upstream response.
 *
 * TODO: For onboarding, modify this to return the Operation ID.
 */
export const refinedListOfBatchInvites = (
  invites: RawBatchInviteNetworkResponse,
): BatchInvite[] =>
  "invites" in invites
    ? invites.invites.map((invite) => ({
        phoneOrEmail: invite.userName,
        role: invite.roleName as Role,
      }))
    : invites.failed_invites.map((invite) => ({
        operationId: invite.operationId,
        phoneOrEmail: invite.userName,
        role: invite.roleName as Role,
        message: invite.message,
      }));
